import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Circle, Download, FileText, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { CertificatePanel } from "@/components/CoachReadyCertificate";
import { SeasonCard } from "@/components/CoachReadySeasonForm";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ProgressBar = ({ done, total }) => (
  <div data-testid="cr-progress">
    <div className="flex justify-between text-xs uppercase tracking-wide font-bold text-[#3A3A3A]">
      <span>Your progress</span><span data-testid="cr-progress-count">{done} of {total} lessons</span>
    </div>
    <div className="mt-2 h-3 bg-[#E7E3D9] border border-[#D3CFC0]">
      <div className="h-full bg-[#E8A33D] transition-[width] duration-500" style={{ width: `${(done / total) * 100}%` }} />
    </div>
  </div>
);

function ResendBox() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) { setState("bad"); return; }
    setState("busy");
    try { await api.post("/coach-ready/resend", { email: email.trim().toLowerCase() }); setState("sent"); }
    catch (ex) { setState(ex?.response?.status === 429 ? "limit" : "error"); }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="cr-resend-page">
      <Seo title="Coach Ready access" brand="VoluntoldHQ" path="/coach-ready/learn" noindex />
      <span className="kicker" style={{ color: "#DB9130" }}>Coach Ready</span>
      <h1 className="mt-2 font-head font-semibold uppercase text-4xl text-[#1E1E1E]">Resend my access link</h1>
      <p className="mt-3 text-[#3A3A3A]">Enter the email you used at checkout. If it has Coach Ready, we&apos;ll email your private link.</p>
      {state === "sent" ? (
        <p className="mt-6 border-2 border-[#1E1E1E] bg-white p-4 text-sm text-[#1E1E1E]" data-testid="cr-resend-sent">Check your inbox. If that email has Coach Ready, your link is on its way.</p>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-3" noValidate>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="cr-resend-email"
            className="w-full border-2 border-[#D3CFC0] bg-white px-4 py-3 focus:outline-none focus:border-[#1E1E1E]" />
          {state === "bad" && <p className="text-sm text-[#B23A2E]">Enter a valid email address.</p>}
          {state === "limit" && <p className="text-sm text-[#B23A2E]">Too many tries. Give it a few minutes.</p>}
          {state === "error" && <p className="text-sm text-[#B23A2E]">Something went wrong. Try again.</p>}
          <button type="submit" disabled={state === "busy"} data-testid="cr-resend-submit"
            className="w-full bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm py-3.5 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
            {state === "busy" ? "Sending…" : "Email me my link"}
          </button>
        </form>
      )}
    </div>
  );
}

const LessonRow = ({ token, l }) => (
  <Link to={`/coach-ready/learn/${token}/${l.n}`} data-testid={`cr-lesson-link-${l.n}`}
    className="flex items-start gap-3 border-2 border-[#D3CFC0] bg-white p-4 hover:border-[#1E1E1E] transition-colors">
    {l.completed ? <CheckCircle2 size={22} className="text-[#2F7A4B] shrink-0 mt-0.5" /> : <Circle size={22} className="text-[#B9B3A5] shrink-0 mt-0.5" />}
    <span className="flex-1">
      <span className="block text-xs uppercase tracking-wide font-bold" style={{ color: "#DB9130" }}>Lesson {l.n}{l.completed ? " · Complete" : ""}</span>
      <span className="block font-head uppercase text-lg text-[#1E1E1E] leading-tight">{l.title}</span>
    </span>
  </Link>
);

const ToolkitPanel = ({ tk }) => (
  <div className="border-2 border-[#1E1E1E] bg-[#1E1E1E] text-[#F5F0EA] p-5" data-testid="cr-toolkit">
    <span className="kicker" style={{ color: "#DB9130" }}>Your Toolkit</span>
    <a href={`${BACKEND}${tk.zip_url}`} data-testid="cr-toolkit-zip"
      className="mt-3 flex items-center justify-center gap-2 bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm py-3 hover:bg-[#D6922C] transition-colors">
      <Download size={16} /> Download all (zip)
    </a>
    <ul className="mt-4 space-y-1.5 text-sm">
      {tk.files.map((f) => (
        <li key={f.url}><a href={`${BACKEND}${f.url}`} className="flex gap-2 hover:text-[#E8A33D]"><FileText size={15} className="mt-0.5 shrink-0" />{f.name}</a></li>
      ))}
    </ul>
  </div>
);

function Dashboard({ token }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/coach-ready/learn/${token}`).then(({ data }) => setD(data)).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load Coach Ready."));
  }, [token]);
  if (err) return (
    <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="cr-error">
      <p className="text-[#3A3A3A]">{err}</p>
      <Link to="/coach-ready/learn" className="mt-4 inline-block link-line" data-testid="cr-error-resend">Resend my access link</Link>
    </div>
  );
  if (!d) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin" size={32} /></div>;
  const next = d.lessons.find((l) => !l.completed);
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-16" data-testid="cr-dashboard">
      <Seo title="Coach Ready" brand="VoluntoldHQ" path="/coach-ready/learn" noindex />
      <span className="kicker" style={{ color: "#DB9130" }}>Coach Ready</span>
      <h1 className="mt-1 font-head font-semibold uppercase text-4xl sm:text-5xl text-[#1E1E1E]">Your lessons</h1>
      <div className="mt-6"><ProgressBar done={d.completed.length} total={d.total} /></div>
      <div className="mt-8 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-start">
        <div className="space-y-3">
          <Link to={`/coach-ready/learn/${token}/0`} data-testid="cr-welcome-link"
            className="block border-2 border-[#1E1E1E] bg-[#F5F0EA] p-4 hover:bg-white transition-colors">
            <span className="block text-xs uppercase tracking-wide font-bold" style={{ color: "#DB9130" }}>Start here</span>
            <span className="block font-head uppercase text-lg text-[#1E1E1E]">{d.welcome.title}</span>
          </Link>
          {d.lessons.map((l) => <LessonRow key={l.n} token={token} l={l} />)}
          {next && <Link to={`/coach-ready/learn/${token}/${next.n}`} data-testid="cr-continue"
            className="mt-2 block text-center bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm py-3.5 hover:bg-[#D6922C] transition-colors">
            {d.completed.length ? "Continue" : "Start"}: Lesson {next.n}</Link>}
          {!next && <CertificatePanel token={token} cert={d.certificate} onChange={(c) => setD({ ...d, certificate: c })} />}
        </div>
        <div className="space-y-4">
          <a href={`${BACKEND}${d.lessons_pdf_url}`} data-testid="cr-lessons-pdf"
            className="flex items-center gap-2 border-2 border-[#D3CFC0] bg-white p-4 text-sm font-bold text-[#1E1E1E] hover:border-[#1E1E1E] transition-colors">
            <Download size={16} /> Download all lessons (PDF)
          </a>
          {d.toolkit && <ToolkitPanel tk={d.toolkit} />}
          <SeasonCard token={token} />
        </div>
      </div>
    </div>
  );
}

export default function CoachReadyLearn() {
  const { token } = useParams();
  return token ? <Dashboard token={token} /> : <ResendBox />;
}
